import { useCallback, useState } from 'react'

/** Lo que dura la animación de salida. Igual que en el CSS de `.saliendo`. */
const DURACION_MS = 320

/**
 * A-16 · Eliminar una fila.
 *
 * Marca la fila que se va, espera a que termine la animación y hasta entonces
 * deja que quien llama borre el registro. Sirve igual si la lista se vuelve a
 * pedir al servidor o si se filtra en memoria: en los dos casos la fila ya
 * salió antes de que el nodo desaparezca.
 *
 *   const { claseDeFila, animarSalida } = useFilaSaliente()
 *   ...
 *   await animarSalida(id)
 *   await borrarEnElServidor(id)
 *   <tr className={claseDeFila(id)}>
 */
export function useFilaSaliente<T extends string | number = string>() {
  const [saliendo, setSaliendo] = useState<T[]>([])

  /** Acepta una fila o varias: hay pantallas que borran en lote. */
  const animarSalida = useCallback((id: T | T[]) => {
    const ids = Array.isArray(id) ? id : [id]
    setSaliendo((previas) => [...new Set([...previas, ...ids])])
    return new Promise<void>((resolver) => setTimeout(resolver, DURACION_MS))
  }, [])

  // Se limpia cuando la fila ya no está en la lista; si el borrado falla, quien
  // llama puede olvidarse de esto y la fila vuelve a su lugar.
  const cancelarSalida = useCallback((id: T | T[]) => {
    const ids = Array.isArray(id) ? id : [id]
    setSaliendo((previas) => previas.filter((otra) => !ids.includes(otra)))
  }, [])

  const claseDeFila = useCallback(
    (id: T) => (saliendo.includes(id) ? 'saliendo' : ''),
    [saliendo],
  )

  return { claseDeFila, animarSalida, cancelarSalida }
}
